/**
 * chat-order-report.tsx — Khối báo cáo "Hiệu quả Chat → Chốt" (màn Tổng quan).
 *
 * Theo bàn giao `tong-quan-BAN-GIAO.md`, có một điều chỉnh so bản gốc:
 *  - Chỉ số 01 đổi từ "Số người đã kết bạn (trong kỳ)" → "Số liên hệ" = TỔNG
 *    liên hệ trong danh bạ hiện có, KHÔNG lọc theo kỳ. Zalo không cấp thời
 *    điểm kết bạn thật cho từng liên hệ (chỉ có lúc hệ thống ĐỒNG BỘ danh bạ),
 *    nên "kết bạn mới trong kỳ" là số bịa — một lần đồng bộ lại từng khiến
 *    báo cáo hiện "154 người kết bạn trong một ngày". Số liên hệ không có
 *    badge trend vì là ảnh chụp hiện tại (current = previous, so sánh vô nghĩa).
 *  - 10 chỉ số chia 2 nhóm (① Tương tác & Nhắn tin / ② Chuyển đổi thành chốt),
 *    số thứ tự 01–12 theo nghiệp vụ (09, 10 chưa dùng).
 *  - Bộ lọc kỳ: Ngày / Tuần / Tháng (mặc định) / Kỳ (so sánh) ▾ / Tùy chỉnh ▾.
 *    Ở chế độ so sánh, mỗi thẻ hiện badge trend ▲/▼ % so kỳ trước.
 *  - Phễu Kết bạn → Nhắn tin → Đơn + tách đơn AI vs Nhân viên.
 *  - Biểu đồ Tin nhắn theo ngày (7 ngày, GET /dashboard/message-volume) và
 *    Tags theo hội thoại (thay Pipeline cũ).
 *
 * API chỉ trả số đếm gốc — mọi tỉ lệ và trend tính tại đây, định dạng vi-VN
 * (13.316 · 25,0% · doanh số VND hiện nguyên số: "2.496.000 ₫").
 *  - Nguồn chốt đơn = ĐƠN ĐỒNG BỘ từ hệ thống ngoài (Evotech…). Chưa kết nối
 *    nguồn nào (meta.orderSource.connected = false) → nhóm ② hiện khối
 *    "Chưa kết nối nguồn đơn hàng" + link Tích hợp thay vì các thẻ số 0.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import {
  ArrowDown, Bot, CalendarRange, ChevronDown, Handshake, MessageCircle, Moon,
  PlugZap, ShoppingCart, TrendingDown, TrendingUp, Users,
} from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { useMessageVolume } from '@/hooks/use-analytics'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { CHANNEL_GROUPS, groupOfPlatform, type ChannelGroupId } from '@/lib/channel-groups'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  useChatReport,
  type ChatReportCompare,
  type ChatReportMetrics,
  type ChatReportMode,
  type ChatReportScope,
} from '@/hooks/use-chat-report'
import type { ChiSoTruyVet } from '@/hooks/use-drilldown'
import { cn, formatNumber } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { currentLocale, i18n } from '@/lib/i18n'
import { chartLabelStep, isWeekView, metricValueClass, tagBarPct } from './report-layout'

// ── Định dạng theo ngôn ngữ đang chọn ────────────────────────────────

/** 25,0% — một số thập phân. Mẫu = 0 → "—" (không chia cho 0). */
function fmtPct1(numer: number, denom: number): string {
  if (!denom) return '—'
  return `${((numer / denom) * 100).toLocaleString(currentLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
}

/**
 * VND nguyên → "2.496.000 ₫".
 *
 * Cố ý KHÔNG rút gọn thành "2 tr" hay "1,31 tỷ": đây là số tiền, người xem cần
 * đối chiếu với sổ sách và với bảng truy vết bên dưới. Làm tròn về triệu biến
 * 2.496.000 thành "2 tr" — sai gần nửa triệu ngay trên thẻ.
 */
function fmtRevenue(vnd: number): string {
  return `${formatNumber(Math.round(vnd))} ₫`
}

/** % an toàn dạng số (cho width thanh tỉ lệ). */
function pctNum(numer: number, denom: number): number {
  if (!denom) return 0
  return Math.max(0, Math.min(100, (numer / denom) * 100))
}

// ── Trend so kỳ trước ───────────────────────────────────────────────

type MetricKey =
  | 'friends' | 'msgIn' | 'msgPerFriend' | 'afterHours' | 'afterHoursPct'
  | 'orders' | 'convRate' | 'revenue' | 'aiOrders' | 'aiPct'

/** 10 giá trị raw để so kỳ (đúng hàm raws() của mock bàn giao). */
function raws(d: ChatReportMetrics): Partial<Record<MetricKey, number>> {
  const r: Partial<Record<MetricKey, number>> = {
    friends: d.friends,
    msgIn: d.msgIn,
    afterHours: d.afterHours,
  }
  if (d.friends > 0) r.msgPerFriend = d.msgIn / d.friends
  if (d.msgIn > 0) r.afterHoursPct = d.afterHours / d.msgIn
  if (d.orders != null) {
    r.orders = d.orders
    if (d.msgIn > 0) r.convRate = d.orders / d.msgIn
  }
  if (d.revenue != null) r.revenue = d.revenue
  if (d.aiOrders != null) {
    r.aiOrders = d.aiOrders
    if (d.orders) r.aiPct = d.aiOrders / d.orders
  }
  return r
}

/** % thay đổi so kỳ trước — null khi thiếu dữ liệu hoặc kỳ trước = 0. */
function trendOf(
  key: MetricKey,
  cur?: Partial<Record<MetricKey, number>>,
  prev?: Partial<Record<MetricKey, number>>,
): number | null {
  const c = cur?.[key]
  const p = prev?.[key]
  if (c == null || p == null || !p) return null
  return ((c - p) / p) * 100
}

function TrendBadge({ change }: { change: number | null }) {
  if (change == null) return null
  const up = change >= 0
  const Icon = up ? TrendingUp : TrendingDown
  return (
    <span
      className={cn(
        'mt-1 inline-flex flex-wrap items-center gap-x-1 text-[11px] font-semibold sm:text-xs',
        up ? 'text-success' : 'text-destructive',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {up ? '+' : '−'}
      {Math.abs(change).toLocaleString(currentLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
      {i18n.t('reports:chatOrder.vsPrevPeriod')}
    </span>
  )
}

// ── Thẻ chỉ số ──────────────────────────────────────────────────────

/** Lưới thẻ chỉ số: điện thoại 2 cột (thẻ lẻ cuối chiếm cả hàng), máy tính 2 → 3 → 5 cột như cũ. */
const METRIC_GRID =
  'grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-3 xl:grid-cols-5 [&>*:last-child:nth-child(odd)]:col-span-2 sm:[&>*:last-child:nth-child(odd)]:col-span-1'

function MetricCard({
  num, label, value, sub, icon: Icon, trend, barPct, accent = 'primary', onClick,
}: {
  num: string
  label: string
  value: string
  sub?: string
  icon?: typeof Handshake
  trend: number | null
  barPct?: number | null
  accent?: 'primary' | 'success' | 'warning' | 'destructive'
  /** Có hàm này thì cả thẻ bấm được để mở bảng truy vết số liệu. */
  onClick?: () => void
}) {
  const accentBorder = {
    primary: 'border-l-primary',
    success: 'border-l-success',
    warning: 'border-l-warning',
    destructive: 'border-l-destructive',
  }[accent]
  const accentIcon = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    destructive: 'bg-destructive/10 text-destructive',
  }[accent]
  const accentBar = {
    primary: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    destructive: 'bg-destructive',
  }[accent]

  return (
    <Card
      className={cn(
        // Điện thoại: thẻ gọn (xếp 2 cột như thẻ số liệu Lark Mobile); từ sm: như cũ.
        'relative border-l-4 p-3 sm:p-4',
        accentBorder,
        onClick && 'cursor-pointer transition-shadow hover:shadow-md',
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } } : undefined}
      title={onClick ? i18n.t('reports:chatOrder.clickHint') : undefined}
    >
      <span className="absolute right-2.5 top-2.5 text-[10px] font-bold text-muted-foreground/50 sm:right-3 sm:top-3 sm:text-[11px]">{num}</span>
      <div className="flex items-start gap-2 pr-5 text-xs font-medium leading-tight text-muted-foreground sm:items-center sm:pr-6">
        {Icon && (
          <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-md', accentIcon)}>
            <Icon className="h-3.5 w-3.5" />
          </span>
        )}
        <span className="min-w-0">{label}</span>
      </div>
      {/* Số tiền nguyên có thể dài tới 15 ký tự — hạ cỡ chữ thay vì để tràn thẻ. */}
      <p className={cn('mt-1.5 font-bold tabular-nums tracking-tight sm:mt-2', metricValueClass(value.length))}>
        {value}
      </p>
      <TrendBadge change={trend} />
      {sub && <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-muted-foreground">{sub}</p>}
      {barPct != null && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div className={cn('h-full rounded-full', accentBar)} style={{ width: `${barPct}%` }} />
        </div>
      )}
    </Card>
  )
}

// ── Bộ lọc kỳ ───────────────────────────────────────────────────────

const compareOption = (value: ChatReportCompare, key: string) => ({
  value,
  get label() { return i18n.t(`reports:chatOrder.compareOptions.${key}`) },
})

const COMPARE_OPTIONS: { value: ChatReportCompare; label: string }[] = [
  compareOption('7d', '7d'),
  compareOption('month', 'month'),
  compareOption('quarter', 'quarter'),
  compareOption('year', 'year'),
]

export function PeriodFilter({ mode, onChange }: { mode: ChatReportMode; onChange: (m: ChatReportMode) => void }) {
  const { t } = useTranslation('reports')
  const [customOpen, setCustomOpen] = useState(false)
  const [from, setFrom] = useState(dayjs().subtract(29, 'day').format('YYYY-MM-DD'))
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'))

  // Tuần/tháng/năm là kỳ TRƯỢT tính đến hết hôm nay (7 / 30 / 365 ngày), không
  // phải theo lịch — chú thích ghi rõ số ngày để không ai hiểu nhầm thành
  // "từ mồng 1" hay "từ 01/01".
  const segs: { key: 'day' | 'week' | 'month' | 'year' | 'all'; label: string; chu: string }[] = [
    { key: 'day', label: t('chatOrder.periods.day'), chu: t('chatOrder.periods.dayHint') },
    { key: 'week', label: t('chatOrder.periods.week'), chu: t('chatOrder.periods.weekHint') },
    { key: 'month', label: t('chatOrder.periods.month'), chu: t('chatOrder.periods.monthHint') },
    { key: 'year', label: t('chatOrder.periods.year'), chu: t('chatOrder.periods.yearHint') },
    { key: 'all', label: t('chatOrder.periods.all'), chu: t('chatOrder.periods.allHint') },
  ]

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex overflow-hidden rounded-md border">
        {segs.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => onChange({ kind: 'period', period: s.key })}
            title={s.chu}
            className={cn(
              'px-3 py-1.5 text-xs font-semibold transition-colors',
              mode.kind === 'period' && mode.period === s.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-accent',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={mode.kind === 'compare' ? 'default' : 'outline'} size="sm">
            {t('chatOrder.comparePeriod')}
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[230px]">
          {COMPARE_OPTIONS.map((o) => (
            <DropdownMenuItem
              key={o.value}
              className={cn(
                mode.kind === 'compare' && mode.compare === o.value && 'bg-primary/10 font-semibold text-primary',
              )}
              onSelect={() => onChange({ kind: 'compare', compare: o.value })}
            >
              {o.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu open={customOpen} onOpenChange={setCustomOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant={mode.kind === 'custom' ? 'default' : 'outline'} size="sm">
            <CalendarRange className="h-3.5 w-3.5" />
            {t('chatOrder.custom')}
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[250px] p-3">
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-muted-foreground">{t('chatOrder.fromDate')}</Label>
              <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('chatOrder.toDate')}</Label>
              <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="mt-1" />
            </div>
            <Button
              className="w-full"
              size="sm"
              disabled={!from || !to || from > to}
              onClick={() => {
                onChange({ kind: 'custom', from, to })
                setCustomOpen(false)
              }}
            >
              {t('chatOrder.apply')}
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

// ── Biểu đồ tin nhắn theo ngày (div thuần, màu theo token) ──────────

/** Chủ Nhật..Thứ Bảy, 0 = Chủ Nhật (khớp `dayjs().day()`) — đọc i18n lúc dùng, không đóng băng lúc nạp module. */
function dayShort(day: number): string {
  const keys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
  return i18n.t(`reports:chatOrder.dayShort.${keys[day]}`)
}

/**
 * Cột tin nhắn theo ngày, trải đúng kỳ đang lọc.
 *
 * Nhãn trục ngang đổi theo độ dài kỳ: kỳ ngắn ghi thứ (CN, T2…) vì người xem
 * nghĩ theo tuần; kỳ dài ghi ngày/tháng và chỉ ghi thưa ra, chứ 30 nhãn chồng
 * nhau thì không đọc được nhãn nào.
 */
function MessageVolumeChart({ mode, scope }: { mode: ChatReportMode; scope?: ChatReportScope }) {
  const { t } = useTranslation('reports')
  const { data, isLoading } = useMessageVolume(mode, scope)
  const isMobile = useIsMobile()
  if (isLoading) return <Loading />
  const days = data?.data ?? []
  const max = Math.max(1, ...days.map((d) => Math.max(d.sent, d.received)))
  const total = days.reduce((s, d) => s + d.sent + d.received, 0)
  const theoTuan = isWeekView(days.length)
  // Kỳ dài thì chỉ ghi nhãn cách quãng — tối đa ~10 nhãn trên máy tính, ~5 trên điện thoại (trục hẹp hơn).
  const buocNhan = chartLabelStep(days.length, isMobile)

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-x-3 gap-y-1.5 space-y-0 p-4 pb-3 sm:p-6 sm:pb-3">
        <div className="min-w-0">
          <CardTitle className="text-sm">{t('chatOrder.messagesByDay')}</CardTitle>
          <CardDescription className="mt-0.5 text-xs">
            {t('chatOrder.messagesByDayDesc', { days: days.length, total: formatNumber(total) })}
            {data?.truncated ? t('chatOrder.truncatedHint') : ''}
          </CardDescription>
        </div>
        <div className="flex gap-3 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-primary" />
            {t('chatOrder.sent')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-primary/35" />
            {t('chatOrder.received')}
          </span>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
        <div className={cn('flex h-40 items-end justify-between sm:h-44', theoTuan ? 'gap-1.5 sm:gap-2' : 'gap-px')}>
          {days.map((d, idx) => (
            <div key={d.date} className="flex h-full min-w-0 flex-1 flex-col items-center gap-1.5">
              <div className={cn('flex w-full flex-1 items-end justify-center', theoTuan ? 'gap-1' : 'gap-px')}>
                <div
                  className={cn('rounded-t-sm bg-primary', theoTuan ? 'w-2 sm:w-2.5' : 'w-full')}
                  style={{ height: `${Math.round((d.sent / max) * 100)}%` }}
                  title={t('chatOrder.sentOnDate', { date: dayjs(d.date).format('DD/MM'), count: formatNumber(d.sent) })}
                />
                <div
                  className={cn('rounded-t-sm bg-primary/35', theoTuan ? 'w-2 sm:w-2.5' : 'w-full')}
                  style={{ height: `${Math.round((d.received / max) * 100)}%` }}
                  title={t('chatOrder.receivedOnDate', { date: dayjs(d.date).format('DD/MM'), count: formatNumber(d.received) })}
                />
              </div>
              <span className="h-3 whitespace-nowrap text-[10px] font-semibold text-muted-foreground">
                {theoTuan
                  ? dayShort(dayjs(d.date).day())
                  : idx % buocNhan === 0
                    ? dayjs(d.date).format('DD/MM')
                    : ''}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// ── Khối chính ──────────────────────────────────────────────────────

export const ALL_SCOPE = '__all__'

/**
 * Bộ lọc phạm vi (kênh tương tác + tài khoản) — dùng ở thanh lọc trên cùng
 * màn Tổng quan.
 *
 * Tách khỏi khối báo cáo vì cả thẻ KPI lẫn báo cáo bên dưới đều ăn theo phạm
 * vi này; để nó nằm trong báo cáo thì người dùng đổi tài khoản mà mấy thẻ ở
 * trên vẫn là số toàn công ty.
 */
export function ScopeFilter({
  channel, accountId, onChange,
}: {
  channel: string
  accountId: string
  onChange: (v: { channel: string; accountId: string }) => void
}) {
  const { t } = useTranslation('reports')
  const accountsQ = useZaloAccounts()
  const accounts = (accountsQ.data ?? []).filter(
    (a) => channel === ALL_SCOPE || groupOfPlatform(a.platform) === (channel as ChannelGroupId),
  )
  return (
    <>
      {/* Kênh tương tác — đổi kênh thì reset tài khoản để không lệch phạm vi */}
      <Select value={channel} onValueChange={(v) => onChange({ channel: v, accountId: ALL_SCOPE })}>
        <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SCOPE}>{t('chatOrder.allChannels')}</SelectItem>
          {CHANNEL_GROUPS.filter((g) => g.id !== 'other').map((g) => (
            <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>
          ))}
          <SelectItem value="other">{t('chatOrder.other')}</SelectItem>
        </SelectContent>
      </Select>
      <Select value={accountId} onValueChange={(v) => onChange({ channel, accountId: v })}>
        <SelectTrigger className="h-8 w-48 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_SCOPE}>{t('chatOrder.allAccounts')}</SelectItem>
          {accounts.map((a) => (
            <SelectItem key={a.id} value={a.id}>{a.displayName || t('chatOrder.unnamed')}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  )
}

/**
 * Tổ chức chưa kết nối nguồn đơn hàng nào (Evotech…): thay cho 5 thẻ số 0,
 * nói rõ vì sao trống và dẫn sang Tích hợp.
 */
function KhoiChuaKetNoiNguonDon() {
  const { t } = useTranslation('reports')
  return (
    <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <PlugZap className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">{t('chatOrder.noOrderSource')}</p>
          <p className="text-xs text-muted-foreground">
            {t('chatOrder.noOrderSourceDesc')}
          </p>
        </div>
      </div>
      <Button asChild size="sm" variant="outline">
        <Link to="/integrations">{t('chatOrder.openIntegrations')}</Link>
      </Button>
    </div>
  )
}

/**
 * Khối báo cáo Chat → Chốt. Kỳ và phạm vi do màn Tổng quan giữ và truyền
 * xuống — một bộ lọc duy nhất cho cả trang.
 */
export function ChatOrderReport({
  mode, scope, onTruyVet,
}: {
  mode: ChatReportMode
  scope: ChatReportScope
  /** Bấm vào một thẻ số → mở bảng truy vết. Hộp thoại do màn Tổng quan giữ. */
  onTruyVet: (chiSo: ChiSoTruyVet) => void
}) {
  const { t } = useTranslation('reports')
  const { data, isLoading, isError } = useChatReport(mode, scope)

  if (isError) return <ErrorState message={t('chatOrder.loadError')} />

  const cur = data?.current
  const prev = data?.previous
  const curRaws = cur ? raws(cur) : undefined
  const prevRaws = prev ? raws(prev) : undefined
  const trendFor = (k: MetricKey) => trendOf(k, curRaws, prevRaws)

  const hasOrders = cur != null && cur.orders != null
  // Chưa có meta (đang tải) coi như đã kết nối để không nháy khối cảnh báo.
  const orderSourceConnected = data?.meta.orderSource ? data.meta.orderSource.connected : true
  const staffOrders = hasOrders && cur.aiOrders != null ? (cur.orders ?? 0) - cur.aiOrders : null
  const aiSharePct = hasOrders && cur.orders ? pctNum(cur.aiOrders ?? 0, cur.orders) : 0
  const tags = data?.tags ?? []
  const maxTag = Math.max(1, ...tags.map((x) => x.count))

  return (
    <div className="space-y-4">
      {/* Tiêu đề — bộ lọc kỳ và phạm vi nằm ở thanh lọc trên cùng trang */}
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="border-l-4 border-primary pl-3 text-base font-bold">
          {t('chatOrder.reportTitle')}
        </h2>
        <span className="text-xs text-muted-foreground">{t('chatOrder.periodLabel', { label: data?.label ?? '…' })}</span>
      </div>

      {data?.meta.scopeNote && (
        <p className="rounded-md bg-warning/10 px-3 py-2 text-xs text-warning">
          {data.meta.scopeNote}
        </p>
      )}

      {isLoading || !cur ? (
        <Loading />
      ) : (
        <>
          {/* ① Tương tác & Nhắn tin */}
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {t('chatOrder.section1')}
          </p>
          <div className={METRIC_GRID}>
            <MetricCard num="01" onClick={() => onTruyVet('friends')} label={t('chatOrder.metrics.friends.label')} icon={Users} accent="primary"
              value={formatNumber(cur.friends)} sub={t('chatOrder.metrics.friends.sub')} trend={null} />
            <MetricCard num="02" onClick={() => onTruyVet('msgIn')} label={t('chatOrder.metrics.msgIn.label')} icon={MessageCircle} accent="primary"
              value={formatNumber(cur.msgIn)} sub={t('chatOrder.metrics.msgIn.sub')} trend={trendFor('msgIn')} />
            <MetricCard num="06" onClick={() => onTruyVet('msgPerFriend')} label={t('chatOrder.metrics.msgPerFriend.label')} icon={TrendingUp} accent="primary"
              value={fmtPct1(cur.msgIn, cur.friends)}
              sub={t('chatOrder.metrics.msgPerFriend.sub')} trend={trendFor('msgPerFriend')} />
            <MetricCard num="07" onClick={() => onTruyVet('afterHours')} label={t('chatOrder.metrics.afterHours.label')} icon={Moon} accent="warning"
              value={formatNumber(cur.afterHours)}
              sub={t('chatOrder.metrics.afterHours.sub', { window: data?.meta.afterHoursWindow ?? '08:00–18:00' })} trend={trendFor('afterHours')} />
            <MetricCard num="08" onClick={() => onTruyVet('afterHoursPct')} label={t('chatOrder.metrics.afterHoursPct.label')} icon={Moon} accent="warning"
              value={fmtPct1(cur.afterHours, cur.msgIn)} sub={t('chatOrder.metrics.afterHoursPct.sub')}
              trend={trendFor('afterHoursPct')} barPct={pctNum(cur.afterHours, cur.msgIn)} />
          </div>

          {/* ② Chuyển đổi thành chốt */}
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {t('chatOrder.section2')}
          </p>
          {!orderSourceConnected ? (
            <KhoiChuaKetNoiNguonDon />
          ) : (
            <>
              <div className={METRIC_GRID}>
                <MetricCard num="03" onClick={() => onTruyVet('orders')} label={t('chatOrder.metrics.orders.label')} icon={ShoppingCart} accent="success"
                  value={cur.orders != null ? formatNumber(cur.orders) : '—'}
                  sub={t('chatOrder.metrics.orders.sub')} trend={trendFor('orders')} />
                <MetricCard num="04" onClick={() => onTruyVet('convRate')} label={t('chatOrder.metrics.convRate.label')} icon={ShoppingCart} accent="success"
                  value={cur.orders != null ? fmtPct1(cur.orders, cur.msgIn) : '—'}
                  sub={cur.orders != null ? t('chatOrder.metrics.convRate.sub', { orders: formatNumber(cur.orders), msgs: formatNumber(cur.msgIn) }) : undefined}
                  trend={trendFor('convRate')}
                  barPct={cur.orders != null ? Math.min(100, pctNum(cur.orders, cur.msgIn) * 5) : null} />
                <MetricCard num="05" onClick={() => onTruyVet('revenue')} label={t('chatOrder.metrics.revenue.label')} icon={TrendingUp} accent="success"
                  value={cur.revenue != null ? fmtRevenue(cur.revenue) : '—'}
                  sub={
                    cur.revenue != null && cur.orders
                      ? t('chatOrder.metrics.revenue.sub', { avg: formatNumber(Math.round(cur.revenue / cur.orders)) })
                      : undefined
                  }
                  trend={trendFor('revenue')} />
                <MetricCard num="11" onClick={() => onTruyVet('aiOrders')} label={t('chatOrder.metrics.aiOrders.label')} icon={Bot} accent="primary"
                  value={cur.aiOrders != null ? formatNumber(cur.aiOrders) : '—'}
                  sub={t('chatOrder.metrics.aiOrders.sub')} trend={trendFor('aiOrders')} />
                <MetricCard num="12" onClick={() => onTruyVet('aiPct')} label={t('chatOrder.metrics.aiPct.label')} icon={Bot} accent="primary"
                  value={cur.aiOrders != null && cur.orders ? fmtPct1(cur.aiOrders, cur.orders) : '—'}
                  sub={
                    cur.aiOrders != null && cur.orders != null
                      ? t('chatOrder.metrics.aiPct.sub', { aiOrders: formatNumber(cur.aiOrders), orders: formatNumber(cur.orders) })
                      : undefined
                  }
                  trend={trendFor('aiPct')} barPct={cur.aiOrders != null && cur.orders ? aiSharePct : null} />
              </div>

              {data?.meta.salesNote && (
                <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                  {data.meta.salesNote}
                </p>
              )}
            </>
          )}

          {/* Phễu + AI vs Nhân viên */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[3fr_2fr]">
            <Card>
              <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-3">
                <CardTitle className="text-sm">{t('chatOrder.funnel.title')}</CardTitle>
                <CardDescription className="text-xs">{t('chatOrder.funnel.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 p-4 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-3 rounded-lg bg-primary p-3 text-primary-foreground">
                  <Users className="h-5 w-5" />
                  <div>
                    <p className="text-xs font-medium opacity-90">{t('chatOrder.funnel.contacts')}</p>
                    <p className="text-lg font-bold tabular-nums">{formatNumber(cur.friends)}</p>
                  </div>
                </div>
                <p className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
                  <ArrowDown className="h-3 w-3" />
                  {t('chatOrder.funnel.whoChatted', { pct: fmtPct1(cur.chatters, cur.friends) })}
                </p>
                <div className="flex items-center gap-3 rounded-lg bg-primary/80 p-3 text-primary-foreground">
                  <Users className="h-5 w-5" />
                  <div>
                    <p className="text-xs font-medium opacity-90">{t('chatOrder.funnel.whoChattedLabel')}</p>
                    <p className="text-lg font-bold tabular-nums">{formatNumber(cur.chatters)}</p>
                  </div>
                </div>
                {hasOrders ? (
                  <>
                    <p className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
                      <ArrowDown className="h-3 w-3" />
                      {t('chatOrder.funnel.whoOrdered', { pct: fmtPct1(cur.orders ?? 0, cur.chatters) })}
                    </p>
                    <div className="flex items-center gap-3 rounded-lg bg-success p-3 text-success-foreground">
                      <ShoppingCart className="h-5 w-5" />
                      <div className="flex-1">
                        <p className="text-xs font-medium opacity-90">{t('chatOrder.metrics.orders.label')}</p>
                        <p className="text-lg font-bold tabular-nums">{formatNumber(cur.orders ?? 0)}</p>
                      </div>
                      {cur.revenue != null && (
                        <span className="rounded-full bg-success-foreground/20 px-3 py-1 text-xs font-bold">
                          {fmtRevenue(cur.revenue)}
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="rounded-lg bg-muted p-3 text-center text-xs text-muted-foreground">
                    {orderSourceConnected ? t('chatOrder.funnel.noOrdersYet') : t('chatOrder.noOrderSource')}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-3">
                <CardTitle className="text-sm">{t('chatOrder.aiVsStaff.title')}</CardTitle>
                <CardDescription className="text-xs">{t('chatOrder.aiVsStaff.description')}</CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                {hasOrders && staffOrders != null && (cur.orders ?? 0) > 0 ? (
                  <>
                    <div className="mb-3 flex h-4 overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-primary" style={{ width: `${aiSharePct}%` }} />
                      <div className="h-full bg-success" style={{ width: `${100 - aiSharePct}%` }} />
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-sm bg-primary" />
                        {t('chatOrder.aiVsStaff.aiOpened')}
                        <b className="ml-auto tabular-nums">{formatNumber(cur.aiOrders ?? 0)}</b>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-sm bg-success" />
                        {t('chatOrder.aiVsStaff.staffClosed')}
                        <b className="ml-auto tabular-nums">{formatNumber(staffOrders)}</b>
                      </div>
                    </div>
                    <p className="mt-3 rounded-md bg-muted p-2.5 text-xs leading-relaxed text-muted-foreground">
                      <Bot className="mr-1 inline h-3.5 w-3.5" />
                      <Trans
                        t={t}
                        i18nKey="chatOrder.aiVsStaff.aiShare"
                        values={{ pct: fmtPct1(cur.aiOrders ?? 0, cur.orders ?? 0) }}
                        components={{ b: <b /> }}
                      />
                    </p>
                  </>
                ) : (
                  <p className="rounded-lg bg-muted p-3 text-center text-xs text-muted-foreground">
                    {orderSourceConnected ? t('chatOrder.aiVsStaff.noData') : t('chatOrder.noOrderSource')}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Biểu đồ */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[3fr_2fr]">
            <MessageVolumeChart mode={mode} scope={scope} />
            <Card>
              <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-3">
                <CardTitle className="text-sm">{t('chatOrder.tags.title')}</CardTitle>
                <CardDescription className="text-xs">
                  {t('chatOrder.tags.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2.5 p-4 pt-0 sm:p-6 sm:pt-0">
                {tags.length === 0 ? (
                  <p className="py-4 text-center text-xs text-muted-foreground">
                    {t('chatOrder.tags.noData')}
                  </p>
                ) : (
                  tags.map((tag) => (
                    // Điện thoại: chip + số ở hàng trên, thanh ở hàng dưới (chip tên dài không đẩy thanh về 0); máy tính: một hàng.
                    <div key={tag.name} className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 sm:flex-nowrap">
                      {/* Chip dùng đúng màu của tag (dữ liệu từ danh sách tag của org) */}
                      <span
                        className="inline-flex min-w-0 max-w-[calc(100%-3.5rem)] shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold sm:max-w-[60%]"
                        style={{ color: tag.color, backgroundColor: tag.bg }}
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
                        <span className="truncate">{tag.name}</span>
                      </span>
                      <span className="order-2 ml-auto w-10 shrink-0 text-right text-xs font-bold tabular-nums sm:order-3 sm:ml-0">
                        {formatNumber(tag.count)}
                      </span>
                      <div className="order-3 h-2 w-full overflow-hidden rounded-full bg-muted sm:order-2 sm:w-auto sm:flex-1">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${tagBarPct(tag.count, maxTag)}%` }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  )
}
